import { Divider, HStack, Skeleton, Stack, Text, VStack } from '@chakra-ui/react';
import React, { useMemo } from 'react';

import Paper from '@/components/ui/Paper';
import { DomainsEmptyState } from '@/components/ui/Placeholders/DomainsEmptyState';

import { MonthlySettlementGroup, SettlementImport, SettlementImportListItem } from '../types';
import { sumAmounts } from '../utils/money';

import { SettlementImportItem } from './SettlementImportItem';

interface SettlementImportsListProps {
  imports: SettlementImport[];
  isLoading: boolean;
  selectedMonth: number;
  selectedYear: number;
}

export const SettlementImportsList: React.FC<SettlementImportsListProps> = ({
  imports,
  isLoading,
  selectedMonth,
  selectedYear,
}) => {
  const monthlyGroup = useMemo<MonthlySettlementGroup>(() => {
    const items: SettlementImportListItem[] = imports.map((imp) => {
      const totalAmount = sumAmounts(Object.values(imp.reportData).map((value) => value.total));
      const onlineTotal = sumAmounts(
        Object.values(imp.reportData).map((value) => value.onlineTotal)
      );
      const facilitiesCount = Object.keys(imp.reportData).length;

      return {
        ...imp,
        totalAmount,
        onlineTotal,
        facilitiesCount,
        reportData: imp.reportData,
      };
    });

    const totalAmount = sumAmounts(items.map((item) => item.totalAmount));
    const onlineTotal = sumAmounts(items.map((item) => item.onlineTotal));
    return {
      month: selectedMonth,
      year: selectedYear,
      imports: items,
      unrecognizedCount: items.reduce((count, item) => count + (item.unrecognizedCount || 0), 0),
      totalAmount,
      onlineTotal,
    };
  }, [imports, selectedMonth, selectedYear]);

  const formatMonthYear = (month: number, year: number) => {
    const date = new Date(year, month - 1);
    const monthName = date.toLocaleDateString('pl-PL', { month: 'long' });
    return {
      month: monthName.charAt(0).toUpperCase() + monthName.slice(1),
      year: year.toString(),
    };
  };

  const formatAmount = (amount: number) => {
    return new Intl.NumberFormat('pl-PL', {
      style: 'currency',
      currency: 'PLN',
    }).format(amount);
  };

  if (isLoading) {
    return (
      <Stack spacing={4}>
        {[1, 2, 3].map((i) => (
          <Skeleton key={i} height="100px" borderRadius="md" />
        ))}
      </Stack>
    );
  }

  if (monthlyGroup.imports.length === 0) {
    return (
      <DomainsEmptyState
        emptyStateMessage={`Brak importów dla ${
          formatMonthYear(selectedMonth, selectedYear).month
        } ${formatMonthYear(selectedMonth, selectedYear).year}`}
      />
    );
  }

  return (
    <Stack spacing={6}>
      <Paper color="black" p={6}>
        <Stack spacing={4}>
          <HStack spacing={2} align="baseline">
            <Text fontSize="3xl" fontWeight="bold" color="purple.700">
              {formatMonthYear(monthlyGroup.month, monthlyGroup.year).month}
            </Text>
            <Text fontSize="xl" color="gray.600">
              {formatMonthYear(monthlyGroup.month, monthlyGroup.year).year}
            </Text>
          </HStack>
          <Divider borderColor="purple.200" />
          {monthlyGroup.unrecognizedCount > 0 && (
            <Text color="orange.800">
              Nierozpoznane wpłaty: {monthlyGroup.unrecognizedCount}. Wykazano je osobno w
              importach.
            </Text>
          )}
          <HStack justify="space-between" align="center" flexWrap="wrap" spacing={8}>
            <VStack align="start" spacing={1}>
              <Text
                fontSize="xs"
                color="gray.600"
                textTransform="uppercase"
                letterSpacing="wider"
                fontWeight="medium"
              >
                Liczba importów
              </Text>
              <HStack align="baseline" spacing={1}>
                <Text fontSize="3xl" fontWeight="bold" lineHeight="1">
                  {monthlyGroup.imports.length}
                </Text>
                <Text fontSize="sm" color="gray.600">
                  {monthlyGroup.imports.length === 1
                    ? 'plik'
                    : monthlyGroup.imports.length < 5
                    ? 'pliki'
                    : 'plików'}
                </Text>
              </HStack>
            </VStack>
            <VStack align={{ base: 'start', md: 'center' }} spacing={1}>
              <Text
                fontSize="xs"
                color="gray.600"
                textTransform="uppercase"
                letterSpacing="wider"
                fontWeight="medium"
              >
                Płatności online
              </Text>
              <Text fontSize="3xl" fontWeight="bold" color="green.600" lineHeight="1">
                {formatAmount(monthlyGroup.onlineTotal)}
              </Text>
            </VStack>
            <VStack align={{ base: 'start', md: 'center' }} spacing={1}>
              <Text
                fontSize="xs"
                color="gray.600"
                textTransform="uppercase"
                letterSpacing="wider"
                fontWeight="medium"
              >
                Płatności przelewem
              </Text>
              <Text fontSize="3xl" fontWeight="bold" color="blue.600" lineHeight="1">
                {formatAmount(monthlyGroup.totalAmount - monthlyGroup.onlineTotal)}
              </Text>
            </VStack>
            <VStack align={{ base: 'start', md: 'end' }} spacing={1}>
              <Text
                fontSize="xs"
                color="gray.600"
                textTransform="uppercase"
                letterSpacing="wider"
                fontWeight="medium"
              >
                Suma przypisanych wpłat
              </Text>
              <Text fontSize="3xl" fontWeight="bold" color="purple.700" lineHeight="1">
                {formatAmount(monthlyGroup.totalAmount)}
              </Text>
            </VStack>
          </HStack>
        </Stack>
      </Paper>

      <Stack spacing={3}>
        {monthlyGroup.imports.map((item) => (
          <SettlementImportItem key={item.id} item={item} />
        ))}
      </Stack>
    </Stack>
  );
};
